<template>
  <Page :fill="false">
    <!-- Header -->
    <div class="bg-background-card rounded-lg shadow-card p-6">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <Button
            icon="fa-solid fa-arrow-left"
            severity="secondary"
            text
            rounded
            v-tooltip.top="'Back to Project Settings'"
            @click="goBack"
          />
          <div class="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
            <i class="fa-solid fa-calendar-day text-lg" />
          </div>
          <div>
            <h1 class="text-2xl font-bold text-primary">Factory Holidays</h1>
            <p class="text-sm text-muted">
              Maintain non-working days for
              <span class="font-medium text-text">{{ calendar?.name ?? 'the project calendar' }}</span>.
            </p>
          </div>
        </div>
        <Button
          label="Edit Calendar"
          icon="fa-solid fa-gear"
          severity="secondary"
          outlined
          :disabled="!calendar"
          @click="showCalendarDialog = true"
        />
      </div>
    </div>

    <!-- Holidays table -->
    <div class="bg-background-card rounded-lg shadow-card overflow-hidden">
      <div class="flex items-center justify-between gap-3 p-4 border-b border-surface-200 dark:border-surface-700">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold">{{ holidays.length }} holiday{{ holidays.length === 1 ? '' : 's' }}</span>
          <SelectButton
            v-model="sourceFilter"
            :options="sourceFilterOptions"
            option-label="label"
            option-value="value"
            :allow-empty="false"
            class="ml-2"
          />
        </div>
        <div class="flex gap-2">
          <Button label="By Weekday" icon="fa-solid fa-calendar-week" severity="secondary" outlined @click="showWeekdayDialog = true" />
          <Button label="Add Holiday" icon="fa-solid fa-plus" @click="openAddHoliday" />
        </div>
      </div>

      <DataTable
        :value="filteredHolidays"
        :loading="isLoading"
        data-key="id"
        striped-rows
        paginator
        :rows="15"
        :rows-per-page-options="[15, 30, 50, 100]"
        class="p-datatable-sm"
      >
        <Column field="date" header="Date" sortable style="width: 140px">
          <template #body="{ data }">
            <span class="font-mono text-sm">{{ data.date }}</span>
          </template>
        </Column>
        <Column field="name" header="Name" sortable />
        <Column field="source" header="Source" sortable style="width: 130px">
          <template #body="{ data }">
            <Tag
              :value="sourceLabel(data.source)"
              :severity="data.source === HolidaySource.Manual ? 'info' : 'secondary'"
            />
          </template>
        </Column>
        <Column field="note" header="Note">
          <template #body="{ data }">
            <span class="text-sm text-muted">{{ data.note || '—' }}</span>
          </template>
        </Column>
        <Column header="Actions" style="width: 110px">
          <template #body="{ data }">
            <div class="flex gap-1">
              <Button
                icon="fa-solid fa-pen"
                severity="info"
                size="small"
                text
                rounded
                v-tooltip.top="'Edit'"
                @click="openEditHoliday(data)"
              />
              <Button
                icon="fa-solid fa-trash"
                severity="danger"
                size="small"
                text
                rounded
                v-tooltip.top="'Remove'"
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="text-center py-12">
            <i class="fa-solid fa-calendar-day text-5xl text-surface-300 mb-3" />
            <p class="text-lg font-semibold text-muted mb-2">No holidays yet</p>
            <p class="text-sm text-muted mb-4">Add a single date or generate weekends by weekday.</p>
            <Button label="Add Holiday" icon="fa-solid fa-plus" @click="openAddHoliday" />
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Dialogs -->
    <HolidayFormDialog
      v-model:visible="showHolidayDialog"
      :holiday="editingHoliday"
      :saving="isSaving"
      @submit="onHolidaySubmit"
    />
    <WeekdayHolidaysDialog
      v-model:visible="showWeekdayDialog"
      :saving="isSaving"
      @submit="onWeekdaySubmit"
    />
    <CalendarDefinitionDialog
      v-model:visible="showCalendarDialog"
      :calendar="calendar"
      :saving="isSaving"
      @submit="onCalendarSubmit"
    />
  </Page>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { useConfirm } from 'primevue/useconfirm';
import { HolidaySource, type HolidayDto, type UpdateFactoryCalendarRequest } from '@/shared/services/factoryCalendar';
import { useHolidayManagement } from '@/features/project-administration/composables/useHolidayManagement';
import type { HolidayFormResult } from '@/features/project-administration/components/HolidayFormDialog.vue';
import type { WeekdayFormResult } from '@/features/project-administration/components/WeekdayHolidaysDialog.vue';

definePage({ meta: { requiresProject: true } });

const router = useRouter();
const confirm = useConfirm();
const {
  calendar,
  holidays,
  isLoading,
  isSaving,
  load,
  addHoliday,
  updateHoliday,
  removeHoliday,
  generateByWeekday,
  removeByWeekday,
  updateDefinition,
} = useHolidayManagement();

const showHolidayDialog = ref(false);
const showWeekdayDialog = ref(false);
const showCalendarDialog = ref(false);
const editingHoliday = ref<HolidayDto | null>(null);

const sourceFilter = ref<'all' | 'manual' | 'generated'>('all');
const sourceFilterOptions = [
  { label: 'All', value: 'all' as const },
  { label: 'Manual', value: 'manual' as const },
  { label: 'Generated', value: 'generated' as const },
];

const filteredHolidays = computed<HolidayDto[]>(() => {
  if (sourceFilter.value === 'manual') return holidays.value.filter((h) => h.source === HolidaySource.Manual);
  if (sourceFilter.value === 'generated') return holidays.value.filter((h) => h.source !== HolidaySource.Manual);
  return holidays.value;
});

onMounted(() => load());

function goBack(): void {
  router.push('/project-administration/project-settings');
}

function sourceLabel(source: HolidaySource): string {
  return source === HolidaySource.Manual ? 'Manual' : 'Generated';
}

function openAddHoliday(): void {
  editingHoliday.value = null;
  showHolidayDialog.value = true;
}

function openEditHoliday(holiday: HolidayDto): void {
  editingHoliday.value = holiday;
  showHolidayDialog.value = true;
}

async function onHolidaySubmit(result: HolidayFormResult): Promise<void> {
  const ok = result.id
    ? await updateHoliday(result.id, { name: result.name, note: result.note })
    : await addHoliday({ date: result.date!, name: result.name, note: result.note });
  if (ok) showHolidayDialog.value = false;
}

function confirmDelete(holiday: HolidayDto): void {
  confirm.require({
    message: `Remove holiday "${holiday.name}" (${holiday.date})?`,
    header: 'Remove Holiday',
    icon: 'fa-solid fa-triangle-exclamation',
    acceptClass: 'p-button-danger',
    accept: () => removeHoliday(holiday.id, holiday.name),
  });
}

async function onWeekdaySubmit(result: WeekdayFormResult): Promise<void> {
  if (result.mode === 'remove') {
    confirm.require({
      message: `Remove generated holidays on the selected weekdays between ${result.rangeStart} and ${result.rangeEnd}? Manual holidays are kept.`,
      header: 'Remove Weekday Holidays',
      icon: 'fa-solid fa-triangle-exclamation',
      acceptClass: 'p-button-danger',
      accept: async () => {
        const ok = await removeByWeekday({
          weekdays: result.weekdays,
          rangeStart: result.rangeStart,
          rangeEnd: result.rangeEnd,
        });
        if (ok) showWeekdayDialog.value = false;
      },
    });
    return;
  }

  const ok = await generateByWeekday({
    weekdays: result.weekdays,
    rangeStart: result.rangeStart,
    rangeEnd: result.rangeEnd,
    name: result.name,
    replaceExistingGenerated: true,
  });
  if (ok) showWeekdayDialog.value = false;
}

async function onCalendarSubmit(request: UpdateFactoryCalendarRequest): Promise<void> {
  const ok = await updateDefinition(request);
  if (ok) showCalendarDialog.value = false;
}
</script>
